import React from "react";
import { Button, IconButton } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import { Link, useSearchParams } from "react-router-dom";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";
import AppTable from "components/Shared/AppTable";

import { useLazyListVehicleMaintenanceQuery } from "store/services/vehicleMaintenanceApi";
import { useAppMeta, useTablePagination } from "hooks";
import { BaseTabs } from "components/Shared/BaseTabs";

import ExportVehicleMaintenanceExcel from "./VehicleMaintenanceComponents/ExportVehicleMaintenanceExcel";
import BaseSelect from "components/Shared/Base/BaseSelect";

const VehicleMaintenanceListScreen = () => {
  const [sortOrder, setSortOrder] = React.useState("createdAt");
  const [sortDirection, setSortDirection] = React.useState("-1");
  const [maintenanceType, setMaintenanceType] = React.useState("");

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [
    {
      title: "Reports",
      path: "/app/crm/reports/list",
    },
    {
      title: "Vehicle Maintenance",
    },
  ];

  const vehicleMaintenanceTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "ID",
      selector: (row) => row?.maintenanceId,
      cell: (row) =>
        row?.maintenanceId ? (
          <HighlightedText text={row?.maintenanceId} highlight={keyword} />
        ) : (
          "N/A"
        ),
    },
    {
      name: "Vehicle",
      selector: (row) => row?.vehicle?.registration_number,
      cell: (row) => (
        <div>
          <span className="text-sm">
            {row?.vehicle?.registration_number || "N/A"}
          </span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.vehicle?.vehicle_id || "N/A"}
          </span>
        </div>
      ),
    },
    {
      name: "Merchant",
      selector: (row) => row?.merchant,
      cell: (row) => (
        <div>
          <span className="text-sm">
            {row?.merchant?.merchantName || "N/A"}
          </span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.merchant?.merchantCode || "N/A"}
          </span>
        </div>
      ),
    },
    {
      name: "Type",
      selector: (row) => row?.maintenanceType,
      cell: (row) =>
        row?.maintenanceType ? (
          <HighlightedText text={row?.maintenanceType} highlight={keyword} />
        ) : (
          "N/A"
        ),
    },

    {
      name: "Total Cost",
      selector: (row) => row?.totalCost,
      cell: (row) => `Rs. ${row?.totalCost?.toFixed(2)}`,
    },
    {
      name: "Condition",
      selector: (row) => row?.maintenanceCondition,
      cell: (row) => row?.maintenanceCondition,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/vehicle-maintenance/${row?._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View Maintenance"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/vehicle-maintenance/${row?._id}/edit`}>
            <IconButton size="small" aria-label="Edit Maintenance">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchVehicleMaintenance,
    { data: vehicleMaintenanceRs, isLoading, isFetching, error },
  ] = useLazyListVehicleMaintenanceQuery();

  React.useEffect(() => {
    const queryParams = {
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      sortedBy: sortOrder,
      sortDirection: sortDirection,
      ...(maintenanceType && { types: maintenanceType }),
    };
    fetchVehicleMaintenance(queryParams);
  }, [
    activeTab,
    fetchVehicleMaintenance,
    currentPage,
    perPage,
    keyword,
    sortOrder,
    sortDirection,
    maintenanceType,
  ]);

  useAppMeta(`${activeTab} VehicleMaintenance`);
  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={vehicleMaintenanceTabs}
          defaultValue={activeTab}
          actions={<ExportVehicleMaintenanceExcel />}
        />

        <TableFilter
          searchLabel="Maintenance ID / Type"
          disabled={isLoading || isFetching}
          initialFilters={2}
        >
          <BaseSelect
            fullWidth
            label="Sort By"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            options={[
              { label: "CreatedAt", value: "createdAt" },
              { label: "UpdatedAt", value: "updatedAt" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />

          <BaseSelect
            fullWidth
            label="Sort Direction"
            value={sortDirection}
            onChange={(e) => setSortDirection(e.target.value)}
            options={[
              { label: "Descending", value: "-1" },
              { label: "Ascending", value: "1" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />

          <BaseSelect
            fullWidth
            label="Maintenance Type"
            value={maintenanceType}
            onChange={(e) => setMaintenanceType(e.target.value)}
            options={[
              { label: "All Types", value: "" },
              { label: "Regular", value: "Regular" },
              { label: "Emergency", value: "Emergency" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </TableFilter>

        <AppTable
          columns={columns}
          data={vehicleMaintenanceRs?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={vehicleMaintenanceRs?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default VehicleMaintenanceListScreen;

const PageActions = () => {
  return (
    <>
      <Link to="/app/crm/vehicle-maintenance/create">
        <Button startIcon={<Add />} variant="contained" color="primary">
          Add Vehicle Maintenance
        </Button>
      </Link>
    </>
  );
};
